import React from "react";

import { css } from "@emotion/core";
import { partial } from "lodash";

import { Button, ButtonGroup, Text } from "@zapier/design-system";

import PaypalButton from "@zapier/payment/esm/components/PaypalButton";
import PaypalInfo from "@zapier/payment/esm/components/PaypalInfo";
import { useSize } from "@zapier/payment/esm/hooks/useSize";
import {
  ApiKeys,
  OverdueInvoice,
  PayPalPaymentSource,
  PaymentSourceInput,
  PaymentSourceType,
  SupportedPaymentMethod,
} from "@zapier/payment/esm/types";

import PaymentMethod from "../PaymentMethod";
import { Aside } from "../shared-ui";

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Call to canel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error, method?: PaymentSourceType) => void;
  /** Call to save current edit */
  onSaveEdit: (paypalDetails: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved paypal details */
  savedSource: PayPalPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
  /** Is there an overdue invoice? */
  overdueInvoice?: OverdueInvoice;
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean;
  /** Call to hide change success message */
  onHideSuccessMessage: () => void;
};

const buttonGroupWrapper = css`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const contentCss = css`
  text-align: center;
`;

const labelCss = css`
  margin-right: 20px;
`;

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaypalInfo paypal={props?.savedSource?.details} />
  ) : (
    <React.Fragment>
      <span css={labelCss}>
        <Text tag="h3" type="paragraph1Bold">
          Connect PayPal
        </Text>
      </span>
    </React.Fragment>
  );

/* Rebrand cleanup required */
const PaymentMethodPaypal: React.FC<Props> = props => {
  const size = useSize();
  const isSmall = size === "small";

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={() => <Aside {...props} />}
      renderLabel={partial(renderLabel, props)}
      overdueInvoice={props.overdueInvoice}
      showChangeSuccessMessage={props.showChangeSuccessMessage}
      onHideSuccessMessage={props.onHideSuccessMessage}
    >
      <div css={contentCss}>
        <Text fontWeight={600} type="paragraph1">
          {props.savedSource
            ? "You can reconnect to use a different PayPal account."
            : "Connect your PayPal account."}
        </Text>
        <div css={buttonGroupWrapper}>
          <ButtonGroup
            columnSize={isSmall ? "100%" : "minmax(100px, 250px)"}
            justify="center"
          >
            <Button
              ariaLabel="Cancel"
              color="secondary"
              isFullWidth={true}
              onClick={props.onCancelEdit}
              size="small"
            >
              Cancel
            </Button>
            <PaypalButton
              //@ts-ignore
              apiKey={props.apiKeys.braintree}
              onAuthorize={props.onSaveEdit}
              onError={props.onErrorEdit}
            />
          </ButtonGroup>
        </div>
      </div>
    </PaymentMethod>
  );
};

export default PaymentMethodPaypal;
